import React from 'react'
import { 
  Calendar, 
  FileText, 
  Users, 
  CheckCircle, 
  AlertCircle, 
  ExternalLink,
  Baby,
  BookOpen,
  GraduationCap,
  Clock,
  MapPin
} from 'lucide-react'
import Card from '../components/ui/Card'
import Button from '../components/ui/Button'

const Admissions = () => {
  const admissionSteps = [
    {
      step: 1,
      title: "Inscripción",
      description: "Completa el formulario de inscripción online según el nivel educativo",
      icon: FileText,
      color: "from-gardenia-red to-red-600"
    },
    {
      step: 2,
      title: "Documentación",
      description: "Entrega los documentos requeridos en las fechas establecidas",
      icon: Users,
      color: "from-gardenia-green to-green-600"
    },
    {
      step: 3,
      title: "Evaluación",
      description: "Proceso de evaluación académica y entrevista familiar",
      icon: CheckCircle,
      color: "from-gardenia-gold to-yellow-500"
    },
    {
      step: 4,
      title: "Admisión",
      description: "Publicación de resultados y proceso de matrícula",
      icon: Calendar,
      color: "from-purple-500 to-purple-600"
    }
  ]

  const programs = [
    {
      icon: Baby,
      title: "Transición 2026",
      subtitle: "Para niños de 5 años",
      description: "Proceso especial para el ingreso a preescolar",
      formLink: "https://forms.gle/QQ4JjHVqZJrdNFgo9",
      color: "from-pink-500 to-purple-500",
      bgColor: "bg-pink-50"
    },
    {
      icon: BookOpen,
      title: "Básica Primaria",
      subtitle: "Grados 1° a 5°",
      description: "Proceso para estudiantes de 6 a 10 años",
      formLink: "https://forms.gle/TLS65F72QjcC6VgMA",
      color: "from-gardenia-green to-green-600",
      bgColor: "bg-green-50"
    },
    {
      icon: GraduationCap,
      title: "Bachillerato",
      subtitle: "Grados 6° a 9°",
      description: "Proceso para estudiantes de 11 a 15 años",
      formLink: "https://forms.gle/TLS65F72QjcC6VgMA",
      color: "from-gardenia-red to-red-600",
      bgColor: "bg-red-50"
    }
  ]

  const requiredDocs = [
    "Fotocopia del registro civil de nacimiento",
    "Fotocopia del documento de identidad del estudiante",
    "Fotocopia de la cédula de los padres o acudientes",
    "Certificados de estudios de años anteriores",
    "Fotocopia del carnet de vacunas actualizado",
    "Certificado médico reciente",
    "Dos fotos tamaño cédula del estudiante",
    "Recibo de servicios públicos reciente"
  ]

  const importantInfo = [
    {
      title: "Disponibilidad de Cupos",
      description: "La admisión está sujeta a los cupos disponibles en cada grado",
      icon: AlertCircle
    },
    {
      title: "Proceso No Vinculante",
      description: "El diligenciamiento del formulario no garantiza la asignación de cupo",
      icon: AlertCircle
    },
    {
      title: "Lista de Admitidos",
      description: "Próximamente se publicará la lista de aspirantes que continúan en el proceso",
      icon: Calendar
    }
  ]

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-gardenia-red/10 to-gardenia-green/10">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <div className="mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r from-gardenia-red to-gardenia-green shadow-2xl mb-6">
                <Calendar className="h-10 w-10 text-white" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gardenia-dark mb-6">
              Admisiones 
              <span className="text-gradient block mt-2">2026</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-8">
              ¡El proceso de admisión para el año escolar 2026 está abierto! 
              Únete a nuestra familia educativa y brinda a tu hijo la mejor educación 
              en la Institución Educativa Distrital Las Gardenias.
            </p>
            <div className="bg-gardenia-gold/20 border border-gardenia-gold rounded-lg p-4 mb-8 max-w-2xl mx-auto">
              <div className="flex items-center space-x-3">
                <AlertCircle className="h-6 w-6 text-gardenia-red flex-shrink-0" />
                <p className="text-gardenia-dark font-medium">
                  Proceso abierto para Transición, Básica Primaria y Bachillerato
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Programs and Forms */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gardenia-dark mb-6">
              Formularios de Inscripción
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Selecciona el formulario correspondiente al nivel educativo de tu hijo 
              para iniciar el proceso de admisión.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
            {programs.map((program, index) => {
              const IconComponent = program.icon
              return (
                <Card key={index} className={`${program.bgColor} text-center`} padding="lg">
                  <div className="mb-6">
                    <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r ${program.color} shadow-xl mb-4`}>
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>
                    <Card.Title className="text-2xl">{program.title}</Card.Title>
                    <Card.Subtitle className="text-gardenia-green">{program.subtitle}</Card.Subtitle>
                  </div>
                  <Card.Content className="mb-6">
                    {program.description}
                  </Card.Content>
                  <Button 
                    href={program.formLink}
                    external={true}
                    className="w-full"
                  >
                    <span className="flex items-center justify-center space-x-2">
                      <span>Ir al Formulario</span>
                      <ExternalLink className="h-4 w-4" />
                    </span>
                  </Button>
                </Card>
              )
            })}
          </div>

          {/* Important Information */}
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-gardenia-dark mb-6 text-center">
              Información Importante
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {importantInfo.map((info, index) => {
                const IconComponent = info.icon
                return (
                  <div key={index} className="flex items-start space-x-3">
                    <IconComponent className="h-6 w-6 text-yellow-600 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gardenia-dark mb-2">{info.title}</h4>
                      <p className="text-gray-600 text-sm">{info.description}</p>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Admission Process */}
      <section className="section-padding bg-gradient-to-b from-gray-50 to-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gardenia-dark mb-6">
              Proceso de Admisión
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Conoce los pasos a seguir para completar exitosamente el proceso de admisión 
              en nuestra institución educativa.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {admissionSteps.map((step, index) => {
              const IconComponent = step.icon
              return (
                <Card key={index} className="text-center hover-lift" variant="bordered">
                  <div className="mb-6">
                    <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r ${step.color} shadow-xl mb-4`}>
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>
                    <div className="text-3xl font-bold text-gardenia-red mb-2">
                      {step.step}
                    </div>
                    <Card.Title className="text-lg">{step.title}</Card.Title>
                  </div>
                  <Card.Content className="text-sm">
                    {step.description}
                  </Card.Content>
                </Card>
              )
            })}
          </div>

          {/* Timeline Visual */}
          <div className="relative mb-16">
            <div className="absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-gardenia-red to-gardenia-green transform -translate-y-1/2 hidden lg:block"></div>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              {admissionSteps.map((step, index) => (
                <div key={index} className="text-center relative">
                  <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white border-4 border-gardenia-red shadow-lg z-10 relative">
                    <span className="text-sm font-bold text-gardenia-red">{step.step}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Required Documents */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gardenia-dark mb-6">
              Documentos Requeridos
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Prepara la siguiente documentación para completar el proceso de admisión.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <Card padding="lg" variant="bordered">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {requiredDocs.map((doc, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-gardenia-green flex-shrink-0 mt-1" />
                    <span className="text-gray-700">{doc}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-2">Nota Importante:</h4>
                    <p className="text-blue-800 text-sm">
                      Todos los documentos deben estar vigentes y legibles. 
                      Los documentos adicionales serán solicitados según el caso específico.
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact and Schedule */}
      <section className="section-padding bg-gradient-to-b from-gray-50 to-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <Card padding="lg">
              <Card.Title className="text-2xl text-gardenia-red mb-6">
                Información de Contacto
              </Card.Title>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <MapPin className="h-5 w-5 text-gardenia-red flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gardenia-dark">Ubicación</h4>
                    <p className="text-gray-600">Calle 98C # 1E-99, Barranquilla, Colombia</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Clock className="h-5 w-5 text-gardenia-green flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gardenia-dark">Horario de Atención</h4>
                    <p className="text-gray-600">
                      Lunes a Viernes<br />
                      6:30 AM - 6:00 PM
                    </p>
                  </div>
                </div>
              </div>
            </Card>

            {/* Next Steps */}
            <Card padding="lg" variant="bordered">
              <Card.Title className="text-2xl text-gardenia-green mb-6">
                Próximos Pasos
              </Card.Title>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-gardenia-red text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5">1</div>
                  <p className="text-gray-700">Completa el formulario de inscripción online</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-gardenia-green text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5">2</div>
                  <p className="text-gray-700">Prepara la documentación requerida</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-gardenia-gold text-gardenia-dark text-xs flex items-center justify-center flex-shrink-0 mt-0.5">3</div>
                  <p className="text-gray-700">Espera la confirmación y siguientes instrucciones</p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="section-padding bg-gradient-to-r from-gardenia-red to-gardenia-green text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            ¿Tienes Preguntas sobre el Proceso?
          </h2>
          <p className="text-lg md:text-xl mb-8 opacity-90 max-w-2xl mx-auto">
            Nuestro equipo está disponible para resolver todas tus dudas sobre 
            el proceso de admisión y ayudarte en cada paso.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button to="/contacto" variant="gold" size="lg">
              Contáctanos
            </Button>
            <Button to="/programas" variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-gardenia-red">
              Ver Nuestros Programas
            </Button>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Admissions